// Google Reviews Service
// Haalt reviews op via Google My Business API en filtert op 4+ sterren

export class GoogleReviewsService {
  constructor() {
    this.placeId = process.env.VUE_APP_GOOGLE_PLACE_ID
    this.apiKey = process.env.VUE_APP_GOOGLE_PLACES_API_KEY
    this.minRating = 1
    this.apiBaseUrl = process.env.VUE_APP_REVIEWS_API_URL || ''
    this.googleScriptPromise = null
  }

  async fetchReviews() {
    const placeId = this.placeId || 'ChIJJ_vTz7DpxkcRb8miL2DOfM0'
    let lastError = null

    if (this.shouldUseServerFallback()) {
      try {
        return await this.fetchViaServer(placeId)
      } catch (error) {
        lastError = error
        console.warn('Server reviews fetch failed, trying browser Places SDK:', error)
      }
    }

    try {
      const browserResult = await this.fetchViaGoogleMapsScript(placeId)
      if (browserResult) {
        return browserResult
      }
    } catch (error) {
      lastError = error
      console.error('Browser Google Places lookup failed:', error)
    }

    return this.getEmptyResult(this.toUserFriendlyError(lastError))
  }

  async fetchViaCORS(placeId) {
    if (!this.apiKey) {
      return null
    }

    const endpoint = `https://maps.googleapis.com/maps/api/place/details/json?place_id=${encodeURIComponent(placeId)}&fields=reviews,rating,user_ratings_total,name&key=${encodeURIComponent(this.apiKey)}`

    const response = await this.withTimeout(fetch(endpoint), 8000, 'CORS fetch timeout')
    if (!response.ok) {
      throw new Error(`CORS fetch returned status ${response.status}`)
    }

    const data = await response.json()

    if (data.status !== 'OK') {
      throw new Error(`Google API status: ${data.status}`)
    }

    const result = data.result || {}
    const rawReviews = Array.isArray(result.reviews) ? result.reviews : []
    const filteredReviews = rawReviews.filter((review) => (review.rating || 0) >= this.minRating)

    return {
      reviews: filteredReviews,
      averageRating: result.rating || 0,
      totalReviews: result.user_ratings_total || 0,
      filteredCount: filteredReviews.length
    }
  }

  shouldUseServerFallback() {
    if (this.apiBaseUrl) {
      return true
    }

    if (typeof window === 'undefined') {
      return true
    }

    return ['localhost', '127.0.0.1'].includes(window.location.hostname)
  }

  async fetchViaGoogleMapsScript(placeId) {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return null
    }

    if (!this.apiKey) {
      return null
    }

    await this.withTimeout(this.loadGoogleMapsScript(), 7000, 'Google Maps script load timeout')

    if (!window.google || !window.google.maps || !window.google.maps.places) {
      return null
    }

    const reviewsSort =
      window.google && window.google.maps && window.google.maps.places && window.google.maps.places.ReviewsSort
        ? window.google.maps.places.ReviewsSort.NEWEST
        : 'newest'

    const placeDetails = await this.withTimeout(new Promise((resolve, reject) => {
      const service = new window.google.maps.places.PlacesService(document.createElement('div'))

      service.getDetails(
        {
          placeId,
          fields: ['reviews', 'rating', 'user_ratings_total', 'name'],
          reviewsSort
        },
        (result, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && result) {
            resolve(result)
            return
          }

          reject(new Error(`Google Places status ${status}`))
        }
      )
    }), 7000, 'Google Places details timeout')

    const rawReviews = Array.isArray(placeDetails.reviews) ? placeDetails.reviews : []
    const filteredReviews = rawReviews.filter((review) => (review.rating || 0) >= this.minRating)

    return {
      reviews: filteredReviews,
      averageRating: placeDetails.rating || 0,
      totalReviews: placeDetails.user_ratings_total || 0,
      filteredCount: filteredReviews.length
    }
  }

  async fetchViaServer(placeId) {
    const normalizedBase = (this.apiBaseUrl || '').replace(/\/+$/, '')
    const isBrowserLocalhost = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)
    const serverBase = normalizedBase && (!normalizedBase.includes('localhost') || isBrowserLocalhost)
      ? normalizedBase
      : ''

    const endpoint = serverBase
      ? (serverBase.endsWith('/api')
          ? `${serverBase}/reviews/${placeId}`
          : `${serverBase}/api/reviews/${placeId}`)
      : `/api/reviews/${placeId}`

    const response = await fetch(endpoint)
    if (!response.ok) {
      throw new Error(`Reviews API status ${response.status}`)
    }

    const contentType = response.headers.get('content-type') || ''
    if (!contentType.toLowerCase().includes('application/json')) {
      throw new Error('Reviews API gaf geen JSON terug. Waarschijnlijk is de API endpoint niet bereikbaar op dit domein.')
    }

    const payload = await response.json()

    // Ondersteunt zowel { success: true, data: {...} } als direct {...}
    const data = payload && payload.data ? payload.data : payload

    const rawReviews = Array.isArray(data.reviews) ? data.reviews : []
    const filteredReviews = rawReviews.filter((review) => (review.rating || 0) >= this.minRating)

    return {
      reviews: filteredReviews,
      averageRating: data.rating || data.averageRating || 0,
      totalReviews: data.totalReviews || data.user_ratings_total || 0,
      filteredCount: filteredReviews.length
    }
  }

  toUserFriendlyError(error) {
    const message = (error && error.message ? error.message : '').toLowerCase()

    if (!message) {
      return 'Onbekende fout bij ophalen van reviews.'
    }

    if (message.includes('api keys with referer restrictions')) {
      return 'API key restrictie: deze key mag niet gebruikt worden voor deze Places API methode.'
    }

    if (message.includes('google places status request_denied')) {
      return 'Google Places toegang geweigerd. Controleer API key, Places API activatie en domeinrestricties.'
    }

    if (message.includes('google maps script failed to load')) {
      return 'Google Maps script kon niet laden. Activeer Maps JavaScript API en Places API, en controleer CSP/adblocker.'
    }

    if (message.includes('google places status not_found')) {
      return 'Place ID niet gevonden. Controleer VUE_APP_GOOGLE_PLACE_ID.'
    }

    if (message.includes('geen json')) {
      return 'Reviews API niet bereikbaar op dit domein. Koppel een geldige API URL of gebruik alleen browser Places API.'
    }

    if (message.includes('timeout')) {
      return 'Google API bereikt niet op tijd - waarschijnlijk geblokkeerd. Check adblocker, CSP headers, firewall, of domeinrestricties in Google Cloud.'
    }

    return error.message
  }

  loadGoogleMapsScript() {
    if (this.googleScriptPromise) {
      return this.googleScriptPromise
    }

    this.googleScriptPromise = new Promise((resolve, reject) => {
      if (window.google && window.google.maps && window.google.maps.places) {
        resolve()
        return
      }

      const existingScript = document.querySelector('script[data-google-maps-places="true"]')
      if (existingScript) {
        if (existingScript.dataset.loaded === 'true') {
          resolve()
          return
        }

        existingScript.addEventListener('load', () => resolve(), { once: true })
        existingScript.addEventListener('error', () => reject(new Error('Google Maps script failed to load')), { once: true })
        return
      }

      const script = document.createElement('script')
      script.dataset.googleMapsPlaces = 'true'
      script.async = true
      script.defer = true
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(this.apiKey)}&libraries=places&language=nl&region=NL`
      script.onload = () => {
        script.dataset.loaded = 'true'
        resolve()
      }
      script.onerror = () => reject(new Error('Google Maps script failed to load'))
      document.head.appendChild(script)
    })

    return this.googleScriptPromise
  }

  withTimeout(promise, timeoutMs, message) {
    let timeoutId

    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error(message))
      }, timeoutMs)
    })

    return Promise.race([promise, timeoutPromise]).finally(() => {
      clearTimeout(timeoutId)
    })
  }

  // Format review data voor display
  formatReview(review) {
    const timestamp = review.time || review.timestamp || Math.floor(Date.now() / 1000)
    const authorName = review.author_name || review.author || 'Google gebruiker'
    const rating = Number(review.rating || 0)

    return {
      id: timestamp,
      author: authorName,
      rating,
      text: review.text || '',
      date: new Date(timestamp * 1000).toLocaleDateString('nl-NL'),
      profilePhoto: review.profile_photo_url || review.profile_photo || null,
      stars: '★'.repeat(rating) + '☆'.repeat(Math.max(0, 5 - rating))
    }
  }

  getEmptyResult(errorMessage = null) {
    return {
      reviews: [],
      averageRating: 0,
      totalReviews: 0,
      filteredCount: 0,
      errorMessage
    }
  }
}
